import React, { FormEvent, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { useReturnTo } from '@/contexts/ReturnToContext'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import AttachFileIcon from '@mui/icons-material/AttachFile'

interface Props {
  boardSlug: string
  taskId: number
  issueId: number
  issueType: 'clarification' | 'review'
}

export function IssueMessageForm({ boardSlug, taskId, issueId, issueType }: Props) {
  const returnTo = useReturnTo()
  const issueSegment = issueType === 'clarification' ? 'clarifications' : 'review_items'
  const paramKey = issueType === 'clarification' ? 'clarification_message' : 'review_item_message'

  const [message, setMessage] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [processing, setProcessing] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files
    if (!selected || selected.length === 0) return
    setFiles(prev => [...prev, ...Array.from(selected)])
    if (fileRef.current) fileRef.current.value = ''
  }

  function removeFile(index: number) {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!message.trim()) return

    const formData = new FormData()
    formData.append(`${paramKey}[message]`, message)
    files.forEach(file => {
      formData.append('attachments[][file]', file)
    })
    formData.append('return_to', returnTo || window.location.pathname)

    setProcessing(true)
    router.post(
      `/tasks/boards/${boardSlug}/tasks/${taskId}/${issueSegment}/${issueId}/messages`,
      formData as any,
      {
        forceFormData: true,
        onSuccess: () => {
          setMessage('')
          setFiles([])
        },
        onFinish: () => setProcessing(false),
      }
    )
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          placeholder="Write a reply..."
          value={message}
          onChange={e => setMessage(e.target.value)}
        />
        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          onChange={handleFilesSelected}
        />
        <IconButton
          size="small"
          onClick={() => fileRef.current?.click()}
          color={files.length > 0 ? 'primary' : 'default'}
          title="Attach files"
        >
          <AttachFileIcon fontSize="small" />
        </IconButton>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={processing || !message.trim()}
          sx={{ flexShrink: 0 }}
        >
          Reply
        </Button>
      </Box>
      {files.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          {files.map((file, i) => (
            <Chip
              key={`${file.name}-${i}`}
              label={file.name}
              size="small"
              onDelete={() => removeFile(i)}
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
